import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { fetchPage } from '@/lists/api'
import { CellValue, cardHref } from '@/lists/cellValue'
import { HistoryPanel } from './HistoryPanel'
import {
  carryOver,
  confirmProbation,
  fetchAssignments,
  fetchBalances,
  fetchBonus,
  fetchDashboard,
  fetchEmployee,
  fetchPersonalData,
  fetchPositions,
  fetchReports,
  settleBonus,
  terminateEmployee,
  transferEmployee,
  type AssignmentView,
  type BalanceView,
  type BonusView,
  type DashboardView,
  type EmployeeCard,
} from './hrApi'
import {
  Field,
  FormModal,
  HrFailure,
  HrSkeleton,
  OutcomeAlerts,
  RefLink,
  Section,
  StatusBadge,
  currentMonth,
  day,
  failureText,
  kpiValue,
  money,
  num,
  parseDay,
  percent,
  text,
  today,
  useOutcome,
  type Translate,
} from './hrShared'
import { RecordPicker } from './RecordPicker'
import { Facts, Panel, RecordHeader, RecordTabs, StateView, type Fact, type HeaderAction, type MenuAction } from './record'
import { initials, labelOf, listPath } from './recordHelpers'
import type { RecordViewProps } from './types'
import '@/styles/record.css'

/**
 * Карточка сотрудника на дизайн-системе (Employee.dc.html).
 *
 * Шапка — кто это (фото-заглушка `.ava-xl`, ФИО, статус), где работает (должность, отдел,
 * руководитель) и как связаться. Действия по весу макета: основное — «Перевести…»,
 * второстепенное — «Начислить премию…», увольнение — в «⋯» за чертой. Вкладки «Действия»
 * больше нет: всё, что раньше жило там, — в шапке.
 *
 * Права повторяют серверные: перевод и подтверждение испытательного срока — `update`,
 * увольнение — `delete` (оно закрывает трудовые отношения и отзывает доступ, и уравнять его
 * с правкой телефона нельзя), премия — `hr.kpi:approve` (это выплата).
 *
 * ## ПДн — по нажатию
 *
 * Паспорт, СНИЛС, ИНН и личный телефон не приходят с карточкой: они лежат шифротекстом и
 * читаются отдельным вызовом с записью в журнал. Автоматический запрос при открытии вкладки
 * заполнил бы журнал просмотрами, которых никто не делал. Сервер расшифровывает все четыре
 * поля **одним** вызовом и пишет **одну** строку аудита, поэтому и кнопка одна: четыре кнопки
 * «Показать» обещали бы поштучный журнал, которого нет.
 *
 * ## Чего здесь нет, хотя макет это рисует
 *
 * - «Учётная запись в системе» и «Выдать доступ»: связи сотрудник → пользователь API карточки
 *   не отдаёт.
 * - «Трудовой договор» (номер, график, место работы): кадровых документов REST не выводит.
 * - Основание увольнения (статья ТК): сервер принимает только дату и номер приказа.
 *
 * Должность и отдел в истории назначений называет сервер (`positionName`, `departmentName`),
 * а должность при переводе выбирается из справочника `GET /hr/positions`.
 * - Штатное «рабочий год» у остатков: сервер ведёт остаток по календарному году.
 */
export function EmployeeRecordView({ recordId, record, context, form }: RecordViewProps) {
  const { t, user, locale } = useApp()
  const values = record.values
  const [tab, setTab] = useState('assignments')
  const [dialog, setDialog] = useState<'transfer' | 'terminate' | 'bonus' | null>(null)

  const mayUpdate = can(user, 'hr.employee', 'update')
  const mayTerminate = can(user, 'hr.employee', 'delete')
  const mayReadLeave = can(user, 'hr.leave', 'read')
  const mayReadKpi = can(user, 'hr.kpi', 'read')
  const mayBonus = can(user, 'hr.kpi', 'approve')

  const employee = useQuery({
    queryKey: ['hr-employee', recordId],
    queryFn: ({ signal }) => fetchEmployee(recordId, signal),
    retry: false,
  })
  const assignments = useQuery({
    queryKey: ['hr-assignments', recordId],
    queryFn: ({ signal }) => fetchAssignments(recordId, signal),
    retry: false,
  })
  const balances = useQuery({
    queryKey: ['hr-balances', recordId],
    queryFn: ({ signal }) => fetchBalances(recordId, signal),
    enabled: mayReadLeave,
    retry: false,
  })
  const reports = useQuery({
    queryKey: ['hr-reports', recordId],
    queryFn: ({ signal }) => fetchReports(recordId, signal),
    retry: false,
  })

  const outcome = useOutcome([
    ['hr-employee', recordId],
    ['hr-assignments', recordId],
    ['record', 'hr.employee', recordId],
    ['list', 'hr.employee'],
  ])
  const probation = useMutation({
    mutationFn: () => confirmProbation(recordId),
    onSuccess: () => outcome.succeed(t('card.hr.emp.probationConfirmed')),
    onError: outcome.fail,
  })

  const listTo = listPath('hr.employee', '/employees')
  if (employee.isLoading) return <HrSkeleton label={t('record.hr.emp.loading')} avatar />
  if (!employee.data) {
    return <HrFailure error={employee.error} listTo={listTo} onRetry={() => void employee.refetch()} />
  }
  const card = employee.data
  const employed = card.status !== 'terminated'
  const current = (assignments.data ?? []).find((row) => row.current) ?? null

  // ------------------------------------------------------------------ шапка

  const meta = [
    labelOf(values, 'position_id') ? (
      <span key="position" className="strong" style={{ color: 'var(--fg)' }}>
        {labelOf(values, 'position_id')}
      </span>
    ) : null,
    labelOf(values, 'department_id') ? (
      <span key="department" className="row" style={{ gap: 6 }}>
        <Icon name="building" size="sm" />
        <RefLink values={values} column="department_id" entity="hr.department" />
      </span>
    ) : null,
    labelOf(values, 'manager_id') ? (
      <span key="manager" className="row" style={{ gap: 6 }}>
        {t('record.hr.emp.manager')} <RefLink values={values} column="manager_id" entity="hr.employee" avatar />
      </span>
    ) : null,
  ]
  const contacts = [
    card.personnelNumber ? (
      <span key="number">
        {t('record.hr.emp.personnelNumber')}{' '}
        <span className="mono" style={{ color: 'var(--fg)' }}>
          {card.personnelNumber}
        </span>
      </span>
    ) : null,
    card.hiredOn ? <span key="tenure">{tenureLine(card, locale, t)}</span> : null,
    card.workPhone ? (
      <a key="phone" href={`tel:${card.workPhone}`} className="row nowrap" style={{ gap: 4 }}>
        <Icon name="phone" size="sm" />
        {card.workPhone}
      </a>
    ) : null,
    card.workEmail ? (
      <a key="mail" href={`mailto:${card.workEmail}`} className="row nowrap" style={{ gap: 4 }}>
        <Icon name="mail" size="sm" />
        {card.workEmail}
      </a>
    ) : null,
  ].filter(Boolean)

  const primary: HeaderAction | null =
    mayUpdate && employed
      ? { id: 'transfer', label: t('record.hr.emp.transfer'), onClick: () => setDialog('transfer') }
      : null
  const secondary: HeaderAction[] =
    mayBonus && employed ? [{ id: 'bonus', label: t('record.hr.bonus.open'), onClick: () => setDialog('bonus') }] : []
  const menu: MenuAction[] = [
    {
      id: 'copy-link',
      label: t('record.copyLink'),
      icon: 'link',
      onSelect: () => void navigator.clipboard?.writeText(window.location.href).catch(() => undefined),
    },
  ]
  // Only somebody on probation can pass it: the domain refuses the rest, and an item that
  // always fails teaches people to ignore failures.
  if (mayUpdate && card.status === 'probation') {
    menu.unshift({
      id: 'probation',
      label: t('card.hr.emp.confirmProbation'),
      icon: 'check',
      onSelect: () => probation.mutate(),
    })
  }
  if (mayTerminate && employed) {
    menu.push({ id: 'terminate', label: t('record.hr.emp.terminate'), icon: 'close', danger: true, onSelect: () => setDialog('terminate') })
  }

  // ------------------------------------------------------------------ факты

  const annual = currentBalance(balances.data ?? [])
  const facts: Fact[] = [
    {
      id: 'position',
      label: t('card.hr.emp.position'),
      value: <span className="t-md" style={{ fontWeight: 600 }}>{labelOf(values, 'position_id') ?? t('record.notSet')}</span>,
      sub: current
        ? [t('record.hr.emp.since', { date: day(current.effectiveFrom, locale) }), current.orderNumber ? t('record.hr.emp.orderNo', { number: current.orderNumber }) : null]
            .filter(Boolean)
            .join(' · ')
        : undefined,
    },
    {
      id: 'department',
      label: t('column.department_id'),
      value: <span className="t-md" style={{ fontWeight: 600 }}>{labelOf(values, 'department_id') ? <RefLink values={values} column="department_id" entity="hr.department" /> : t('record.notSet')}</span>,
    },
    {
      id: 'manager',
      label: t('record.hr.emp.managerFact'),
      value: labelOf(values, 'manager_id') ? <RefLink values={values} column="manager_id" entity="hr.employee" avatar /> : t('record.notSet'),
    },
    {
      id: 'tenure',
      label: t('record.hr.emp.tenure'),
      value: card.hiredOn ? tenure(card.hiredOn, t) : '—',
      sub: card.hiredOn ? t('record.hr.emp.since', { date: day(card.hiredOn, locale) }) : undefined,
    },
  ]
  if (mayReadLeave) {
    facts.push({
      id: 'leave',
      label: t('record.hr.emp.leaveLeft'),
      value: annual ? t('record.hr.days', { days: num(annual.main.remaining, locale) }) : '—',
      sub: annual
        ? annual.others.length > 0
          ? t('record.hr.emp.leaveOthers', {
              days: num(annual.others.reduce((sum, row) => sum + row.remaining, 0), locale),
            })
          : t(`hr.leave.type.${annual.main.type}`)
        : balances.isError
          ? t('record.hr.emp.leaveFailed')
          : t('record.hr.emp.leaveNone'),
    })
  }

  const tabs = [
    { id: 'assignments', label: t('card.tab.hrAssignments'), count: assignments.data?.length ?? null },
    ...(mayReadLeave ? [{ id: 'leave', label: t('card.tab.hrBalances') }] : []),
    { id: 'team', label: t('card.tab.hrReports'), count: reports.data?.length ?? null },
    ...(mayReadKpi ? [{ id: 'kpi', label: t('card.tab.hrKpi') }] : []),
    {
      id: 'personal',
      label: (
        <>
          <Icon name="lock" size="sm" />
          {t('card.tab.hrPersonalData')}
        </>
      ),
    },
    { id: 'history', label: t('record.tab.history') },
  ]

  return (
    <div className="rec-page">
      <nav className="crumbs" aria-label={t('record.crumbs')}>
        <Link to={listTo}>{t('nav.employees')}</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{card.fullName}</span>
      </nav>
      <div className="hr-identity">
        <span className="ava-xl" aria-hidden="true">
          {initials(`${card.lastName} ${card.firstName}`)}
        </span>
        <div className="hr-identity-main">
          <RecordHeader
            title={
              <span className="row" style={{ gap: 10 }}>
                {card.fullName}
                <StatusBadge entity="hr.employee" status={card.status} />
              </span>
            }
            meta={meta}
            primary={primary}
            secondary={secondary}
            menu={menu}
            menuLabel={t('record.more')}
          />
          {contacts.length > 0 && <div className="rec-meta t-sm hr-contacts">{contacts}</div>}
        </div>
      </div>

      <OutcomeAlerts failure={outcome.failure} notice={outcome.notice} />

      <Facts items={facts} ariaLabel={t('record.facts')} />

      <RecordTabs tabs={tabs} active={tab} onChange={setTab} ariaLabel={t('record.hr.emp.sections')}>
        <div className="layout-2-wide" style={{ paddingTop: 14 }}>
          <div className="rec-col">
            {tab === 'assignments' && <AssignmentsPanel recordId={recordId} currentPosition={labelOf(values, 'position_id')} currentPositionId={text(values, 'position_id')} />}
            {tab === 'leave' && <LeavePanel employeeId={recordId} />}
            {tab === 'team' && <ReportsPanel recordId={recordId} name={card.fullName} />}
            {tab === 'kpi' && <KpiPanel employeeId={recordId} />}
            {tab === 'personal' && <PersonalDataPanel recordId={recordId} values={values} columns={context.columns} />}
            {tab === 'history' && (
              <Panel title={t('record.history.changes')}>
                <HistoryPanel entity={context.entity} recordId={recordId} />
              </Panel>
            )}
          </div>
          <div className="rec-col">
            <Panel title={t('record.hr.details')}>{form}</Panel>
            <EventsPanel rows={assignments.data ?? []} loading={assignments.isLoading} failed={assignments.isError} />
          </div>
        </div>
      </RecordTabs>

      {dialog === 'transfer' && (
        <TransferDialog
          card={card}
          current={current}
          values={values}
          onClose={() => setDialog(null)}
          onDone={(date) => {
            setDialog(null)
            outcome.succeed(t('record.hr.emp.transferred', { date: day(date, locale) }))
          }}
        />
      )}
      {dialog === 'terminate' && (
        <TerminateDialog
          card={card}
          onClose={() => setDialog(null)}
          onDone={(date) => {
            setDialog(null)
            outcome.succeed(t('record.hr.emp.terminated', { date: day(date, locale) }))
          }}
        />
      )}
      {dialog === 'bonus' && (
        <BonusDialog
          employeeId={recordId}
          name={card.fullName}
          period={currentMonth()}
          onClose={() => setDialog(null)}
          onDone={(bonus) => {
            setDialog(null)
            outcome.succeed(t('record.hr.bonus.settled', { amount: money(bonus.bonus, card.currency ?? null, locale) }))
          }}
        />
      )}
    </div>
  )
}

// ------------------------------------------------------------------ назначения

/**
 * История назначений.
 *
 * История, а не текущее состояние: перевод закрывает прошлое назначение днём раньше и
 * открывает следующее — расчёт за прошлый период обязан считаться по тем условиям, что
 * действовали тогда. «Текущее» приходит с сервера: вычислять его здесь по датам значило бы
 * завести второй ответ на вопрос «какое назначение действует».
 *
 * Оклад закрыт полевым правом: без него сервер шлёт `null`, и рисуется прочерк, не ноль.
 */
export function AssignmentsPanel({
  recordId,
  currentPosition = null,
  currentPositionId = null,
}: {
  recordId: string
  currentPosition?: string | null
  currentPositionId?: string | null
}) {
  const { t, locale } = useApp()
  const query = useQuery({
    queryKey: ['hr-assignments', recordId],
    queryFn: ({ signal }) => fetchAssignments(recordId, signal),
    retry: false,
  })
  const { user } = useApp()

  return (
    <Panel
      title={t('record.hr.emp.assignments')}
      count={query.data?.length ?? null}
      flush
      actions={<span className="t-sm muted">{t('record.hr.emp.salaryNote')}</span>}
    >
      <Section query={query} errorTitle={t('record.hr.emp.assignmentsFailed')}>
        {(rows: AssignmentView[]) =>
          rows.length === 0 ? (
            <StateView compact kind="empty" title={t('card.hr.emp.noAssignments')} description={t('record.hr.emp.noAssignmentsText')} />
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('record.hr.emp.period')}</th>
                  <th>{t('record.hr.emp.event')}</th>
                  <th>{t('card.hr.emp.position')}</th>
                  <th>{t('column.department_id')}</th>
                  <th className="num">{t('card.hr.emp.salary')}</th>
                  <th className="num">{t('card.hr.emp.rate')}</th>
                  <th>{t('card.hr.emp.order')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const departmentHref = row.departmentId ? cardHref(user, 'hr.department', row.departmentId) : null
                  const departmentName = row.departmentName ?? '—'
                  return (
                    <tr key={row.id}>
                      {/* An open end is "to date" in words, not a blank cell: a blank reads
                          as a missing value. */}
                      <td className="nowrap">
                        {day(row.effectiveFrom, locale)} – {row.effectiveTo ? day(row.effectiveTo, locale) : t('card.hr.emp.open')}
                      </td>
                      <td>
                        <span className="row" style={{ gap: 6 }}>
                          {row.reason ? t(`hr.assignment.reason.${row.reason}`) : '—'}
                          {row.current && <span className="badge b-accent">{t('record.hr.emp.current')}</span>}
                        </span>
                      </td>
                      <td>{row.positionName ?? (row.positionId && row.positionId === currentPositionId && currentPosition ? currentPosition : '—')}</td>
                      <td>{departmentHref && departmentName !== '—' ? <Link to={departmentHref}>{departmentName}</Link> : departmentName}</td>
                      <td className="num">{money(row.salary, row.currency, locale)}</td>
                      <td className="num">{num(row.employmentRate, locale)}</td>
                      <td className="mono">{row.orderNumber ?? '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )
        }
      </Section>
    </Panel>
  )
}

/** Кадровые события — та же история назначений лентой: приём, переводы, оклад. */
function EventsPanel({ rows, loading, failed }: { rows: AssignmentView[]; loading: boolean; failed: boolean }) {
  const { t, locale } = useApp()
  const ordered = [...rows].sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))
  return (
    <Panel title={t('record.hr.emp.events')}>
      {loading ? (
        <p className="muted">{t('app.loading')}</p>
      ) : failed ? (
        <StateView compact kind="error" title={t('record.hr.emp.assignmentsFailed')} />
      ) : ordered.length === 0 ? (
        <p className="secondary">{t('record.hr.emp.noEvents')}</p>
      ) : (
        <div className="feed">
          {ordered.map((row, index) => (
            <div key={row.id} className={index === ordered.length - 1 ? 'feed-item is-last' : 'feed-item'}>
              <span className="feed-ico stage">
                <Icon name={row.reason === 'hire' ? 'user' : 'arrow-right'} />
              </span>
              <div className="stack" style={{ minWidth: 0 }}>
                <div className="feed-head">
                  <span className="strong">{row.reason ? t(`hr.assignment.reason.${row.reason}`) : t('record.hr.emp.event')}</span>
                  <span className="t">{day(row.effectiveFrom, locale)}</span>
                </div>
                <span className="t-sm secondary">
                  {[row.orderNumber ? t('record.hr.emp.orderNo', { number: row.orderNumber }) : null, row.salary != null ? money(row.salary, row.currency, locale) : null]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  )
}

// ------------------------------------------------------------------ отпуска

/**
 * Остатки отпусков и ближайшая заявка.
 *
 * Перенесённые дни — **отдельной колонкой**, а не прибавленными к норме: закон относится к
 * ним иначе, и сумма не отвечает на вопрос, сколько дней этого года человек отгулял.
 */
export function BalancesPanel({ employeeId }: { employeeId: string }) {
  const { t, user, locale } = useApp()
  const outcome = useOutcome([['hr-balances', employeeId]])
  const query = useQuery({
    queryKey: ['hr-balances', employeeId],
    queryFn: ({ signal }) => fetchBalances(employeeId, signal),
    retry: false,
  })
  const mayUpdate = can(user, 'hr.leave', 'update')
  const carry = useMutation({
    mutationFn: (balance: BalanceView) => carryOver({ employeeId, fromYear: balance.year, type: balance.type }),
    onSuccess: () => outcome.succeed(t('card.hr.leave.carriedOverDone')),
    onError: outcome.fail,
  })

  return (
    <Panel title={t('record.hr.leave.balances')} flush actions={<span className="t-sm muted">{t('record.hr.leave.balancesNote')}</span>}>
      {(outcome.failure || outcome.notice) && (
        <div className="panel-b">
          <OutcomeAlerts failure={outcome.failure} notice={outcome.notice} />
        </div>
      )}
      <Section query={query} errorTitle={t('record.hr.leave.balancesFailed')}>
        {(rows: BalanceView[]) =>
          rows.length === 0 ? (
            <StateView compact kind="empty" title={t('card.hr.leave.noBalances')} description={t('record.hr.leave.noBalancesText')} />
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('column.leave_type')}</th>
                  <th className="num">{t('card.hr.leave.year')}</th>
                  <th className="num">{t('card.hr.leave.entitled')}</th>
                  <th className="num">{t('card.hr.leave.carriedOver')}</th>
                  <th className="num">{t('card.hr.leave.used')}</th>
                  <th className="num">{t('card.hr.leave.remaining')}</th>
                  {mayUpdate && <th />}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={`${row.year}-${row.type}`}>
                    <td>{t(`hr.leave.type.${row.type}`)}</td>
                    <td className="num">{row.year}</td>
                    <td className="num">{num(row.entitled, locale)}</td>
                    <td className="num">{num(row.carriedOver, locale)}</td>
                    <td className="num">{num(row.used, locale)}</td>
                    <td className="num strong">{num(row.remaining, locale)}</td>
                    {mayUpdate && (
                      <td className="num">
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={carry.isPending || row.remaining <= 0}
                          onClick={() => carry.mutate(row)}
                        >
                          {t('card.hr.leave.carryOver')}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </Section>
    </Panel>
  )
}

function LeavePanel({ employeeId }: { employeeId: string }) {
  const { t, user, locale } = useApp()
  // The nearest request comes through the list mechanism: same scope as the leave list, and
  // the card links to the request's own card, where the decision is made.
  const nearest = useQuery({
    queryKey: ['hr-nearest-leave', employeeId],
    retry: false,
    queryFn: ({ signal }) =>
      fetchPage(
        'hr.leave',
        {
          columns: ['leave_type', 'start_date', 'end_date', 'days_count', 'status'],
          filters: [
            { column: 'employee_id', operator: 'eq', value: employeeId },
            { column: 'end_date', operator: 'gte', value: today() },
            { column: 'status', operator: 'in', value: ['pending', 'approved'] },
          ],
          sort: [{ column: 'start_date', descending: false }],
          pageSize: 1,
        },
        null,
        signal,
      ),
  })
  const row = nearest.data?.rows[0]
  const href = row ? cardHref(user, 'hr.leave', row.id) : null
  return (
    <>
      <BalancesPanel employeeId={employeeId} />
      <Panel title={t('record.hr.leave.nearest')} actions={row ? <StatusBadge entity="hr.leave" status={text(row, 'status')} /> : undefined}>
        {nearest.isLoading ? (
          <p className="muted">{t('app.loading')}</p>
        ) : nearest.isError ? (
          <StateView compact kind="error" title={t('record.hr.leave.nearestFailed')} />
        ) : !row ? (
          <p className="secondary">{t('record.hr.leave.noNearest')}</p>
        ) : (
          <div className="stack" style={{ gap: 10 }}>
            <div className="row-wrap" style={{ gap: '6px 16px' }}>
              <span className="strong t-md">
                {day(text(row, 'start_date'), locale)} – {day(text(row, 'end_date'), locale)}
              </span>
              <span className="secondary">
                <CellValue values={row} column="leave_type" descriptor={{ name: 'leave_type', type: 'text', sortable: false, filterable: false }} entity="hr.leave" />
                {' · '}
                {t('record.hr.leave.workingDays', { days: num(typeof row.days_count === 'number' ? row.days_count : Number(row.days_count ?? NaN), locale) })}
              </span>
            </div>
            {href && (
              <div className="row">
                <Link to={href} className="btn btn-secondary btn-sm">
                  {t('record.hr.leave.openRequest')}
                </Link>
              </div>
            )}
          </div>
        )}
      </Panel>
    </>
  )
}

// ------------------------------------------------------------------ подчинённые

/**
 * Прямые подчинённые.
 *
 * У сотрудника может не быть учётной записи, и это нормально: руководитель склада без логина
 * всё равно руководитель. Отсутствие логина сказывается в маршруте согласования, а не здесь.
 */
export function ReportsPanel({ recordId, name }: { recordId: string; name?: string }) {
  const { t, user, locale } = useApp()
  const query = useQuery({
    queryKey: ['hr-reports', recordId],
    queryFn: ({ signal }) => fetchReports(recordId, signal),
    retry: false,
  })
  return (
    <Panel title={t('card.tab.hrReports')} count={query.data?.length ?? null} flush>
      <Section query={query} errorTitle={t('record.hr.emp.reportsFailed')}>
        {(rows: EmployeeCard[]) =>
          rows.length === 0 ? (
            <StateView compact kind="empty" title={t('card.hr.emp.noReports')} description={t('record.hr.emp.noReportsText', { name: name ?? '' })} />
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('card.hr.emp.name')}</th>
                  <th>{t('column.personnel_number')}</th>
                  <th>{t('column.hired_on')}</th>
                  <th>{t('column.status')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const href = cardHref(user, 'hr.employee', row.id)
                  return (
                    <tr key={row.id}>
                      <td>
                        <span className="row" style={{ gap: 8 }}>
                          <span className="ava" aria-hidden="true">
                            {initials(`${row.lastName} ${row.firstName}`)}
                          </span>
                          {href ? <Link to={href}>{row.fullName}</Link> : row.fullName}
                        </span>
                      </td>
                      <td className="mono muted">{row.personnelNumber ?? '—'}</td>
                      <td>{day(row.hiredOn, locale)}</td>
                      <td>
                        <StatusBadge entity="hr.employee" status={row.status} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )
        }
      </Section>
    </Panel>
  )
}

// ------------------------------------------------------------------ KPI

/**
 * KPI за текущий год.
 *
 * Спрашивают «как идут дела», а не «как было в квартале»: разрез по периодам — на карточке
 * цели KPI. Премии здесь нет — её считают от фонда, и фонд вводится при начислении.
 */
function KpiPanel({ employeeId }: { employeeId: string }) {
  const { t, locale } = useApp()
  const year = new Date().getFullYear()
  const from = `${year}-01-01`
  const to = `${year}-12-31`
  const query = useQuery({
    queryKey: ['hr-kpi-dashboard', employeeId, from, to],
    queryFn: ({ signal }) => fetchDashboard(employeeId, from, to, signal),
    retry: false,
  })
  return (
    <Panel title={t('record.hr.emp.kpiYear', { year })} flush>
      <Section query={query} errorTitle={t('record.hr.kpi.failed')}>
        {(data: DashboardView) =>
          data.lines.length === 0 ? (
            <StateView compact kind="empty" title={t('card.hr.kpi.noTargets')} description={t('record.hr.kpi.noTargetsText')} />
          ) : (
            <>
              <KpiLines data={data} />
              <div className="panel-f">
                <span className="secondary">{t('card.hr.kpi.overall', { percent: num(data.overallPercent, locale) })}</span>
                <span className="spacer" />
                <span className="muted">{t('card.hr.kpi.bonusElsewhere')}</span>
              </div>
            </>
          )
        }
      </Section>
    </Panel>
  )
}

/** Строки дашборда KPI: план и факт двумя числами, выполнение — шкалой и словом. */
export function KpiLines({ data, weights }: { data: DashboardView; weights?: boolean }) {
  const { t, locale } = useApp()
  return (
    <table className="tbl" aria-label={t('record.hr.kpi.lines')}>
      <thead>
        <tr>
          <th>{t('card.hr.kpi.metric')}</th>
          {weights && <th className="num">{t('card.hr.kpi.weight')}</th>}
          <th className="num">{t('column.target_value')}</th>
          <th className="num">{t('column.actual_value')}</th>
          <th>{t('card.hr.kpi.completion')}</th>
        </tr>
      </thead>
      <tbody>
        {data.lines.map((line) => (
          <tr key={line.code}>
            <td className="wrap">{line.name}</td>
            {weights && <td className="num">{percent(line.weight * 100, locale)}</td>}
            <td className="num">{kpiValue(line.target, line.unit, locale)}</td>
            <td className="num strong">{kpiValue(line.actual, line.unit, locale)}</td>
            <td>
              <span className="row" style={{ gap: 8 }}>
                <span className={line.met ? 'meter is-success' : 'meter'} style={{ width: 96 }} aria-hidden="true">
                  <i style={{ width: `${Math.min(Math.max(line.completionPercent ?? 0, 0), 100)}%` }} />
                </span>
                <span className="num" style={{ minWidth: 48 }}>
                  {percent(line.completionPercent, locale)}
                </span>
                {line.completionPercent == null ? (
                  <span className="flag flag-muted">
                    <Icon name="clock" />
                    {t('record.hr.kpi.noFact')}
                  </span>
                ) : line.met ? (
                  <span className="flag flag-success">
                    <Icon name="check" />
                    {t('record.hr.kpi.met')}
                  </span>
                ) : (
                  <span className="flag flag-warning">
                    <Icon name="alert" />
                    {t('record.hr.kpi.below')}
                  </span>
                )}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// ------------------------------------------------------------------ ПДн

/**
 * Персональные данные — по нажатию, одной кнопкой (см. описание карточки).
 *
 * Расшифрованное скрывается через минуту: значение, оставленное на экране, видит каждый, кто
 * проходит мимо стола, а повторный показ — новая строка журнала, и это верно.
 */
export function PersonalDataPanel({
  recordId,
  values = {},
  columns = [],
}: {
  recordId: string
  values?: Record<string, unknown>
  columns?: { name: string }[]
}) {
  const { t, locale } = useApp()
  const queryClient = useQueryClient()
  const [revealed, setRevealed] = useState(false)
  const query = useQuery({
    queryKey: ['hr-personal-data', recordId],
    queryFn: ({ signal }) => fetchPersonalData(recordId, signal),
    enabled: revealed,
    retry: false,
    gcTime: 0,
  })

  useEffect(() => {
    if (!revealed || !query.data) return
    const timer = window.setTimeout(() => {
      setRevealed(false)
      queryClient.removeQueries({ queryKey: ['hr-personal-data', recordId] })
    }, 60_000)
    return () => window.clearTimeout(timer)
  }, [revealed, query.data, queryClient, recordId])

  const has = (column: string) => columns.some((candidate) => candidate.name === column)
  const data = revealed ? query.data : undefined
  const fields: { key: 'passport' | 'snils' | 'inn' | 'personalPhone'; label: string }[] = [
    { key: 'passport', label: t('card.hr.emp.passport') },
    { key: 'snils', label: t('card.hr.emp.snils') },
    { key: 'inn', label: t('card.hr.emp.inn') },
    { key: 'personalPhone', label: t('card.hr.emp.personalPhone') },
  ]

  return (
    <Panel title={t('card.tab.hrPersonalData')}>
      <div className="stack" style={{ gap: 12 }}>
        <div className="alert alert-neutral" style={{ padding: '6px 10px' }}>
          <Icon name="lock" />
          <span>{t('card.hr.emp.pdnHidden')}</span>
        </div>
        {(has('birth_date') || has('gender')) && (
          <dl className="kv" style={{ gridTemplateColumns: '180px minmax(0, 1fr)' }}>
            {has('birth_date') && (
              <>
                <dt>{t('column.birth_date')}</dt>
                <dd>{day(text(values, 'birth_date'), locale)}</dd>
              </>
            )}
            {has('gender') && (
              <>
                <dt>{t('column.gender')}</dt>
                <dd>
                  {text(values, 'gender') ? (
                    <CellValue values={values} column="gender" descriptor={{ name: 'gender', type: 'text', sortable: false, filterable: false }} entity="hr.employee" />
                  ) : (
                    '—'
                  )}
                </dd>
              </>
            )}
          </dl>
        )}
        {fields.map((field) => (
          <div key={field.key} className="secret">
            <span className="stack" style={{ gap: 0 }}>
              <span className="t-sm muted">{field.label}</span>
              {data ? <span className="mono">{data[field.key] ?? t('record.hr.pdn.empty')}</span> : <span className="mask">••••••••••</span>}
            </span>
          </div>
        ))}
        {!revealed ? (
          <div className="row" style={{ gap: 10 }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setRevealed(true)}>
              <Icon name="lock" size="sm" />
              {t('card.hr.emp.pdnReveal')}
            </button>
            <span className="t-xs muted">{t('record.hr.pdn.oneCall')}</span>
          </div>
        ) : query.isLoading ? (
          <p className="muted">{t('app.loading')}</p>
        ) : query.isError ? (
          <StateView compact kind={'error'} title={t('record.hr.pdn.failed')} description={failureText(query.error, t)} />
        ) : (
          <span className="t-xs muted">{t('record.hr.pdn.hidesIn')}</span>
        )}
      </div>
    </Panel>
  )
}

// ------------------------------------------------------------------ окна

/**
 * Перевод: новые условия с даты.
 *
 * Возвращает строку истории, а не карточку: перевод не правит сотрудника, он закрывает
 * прошлое назначение и открывает следующее. Вид изменения (перевод, повышение, оклад) выводит
 * сервер из того, что поменялось, — клиент его не присылает.
 */
function TransferDialog({
  card,
  current,
  values,
  onClose,
  onDone,
}: {
  card: EmployeeCard
  current: AssignmentView | null
  values: Record<string, unknown>
  onClose: () => void
  onDone: (effectiveFrom: string) => void
}) {
  const { t, locale } = useApp()
  const currentDepartment = text(values, 'department_id')
  const [department, setDepartment] = useState<{ id: string; label: string } | null>(
    currentDepartment ? { id: currentDepartment, label: labelOf(values, 'department_id') ?? currentDepartment } : null,
  )
  const salaryNow = current?.salary ?? card.salary
  const [salary, setSalary] = useState(salaryNow == null || salaryNow === undefined ? '' : String(salaryNow))
  const [currency, setCurrency] = useState(current?.currency ?? card.currency ?? '')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [orderNumber, setOrderNumber] = useState('')
  const [failure, setFailure] = useState<string | null>(null)
  const [position, setPosition] = useState<string | null>(card.positionId ?? null)
  const positions = useQuery({ queryKey: ['hr-positions'], queryFn: ({ signal }) => fetchPositions(signal), retry: false })

  const parsedSalary = Number(salary.replace(',', '.').replace(/\s/g, ''))
  const ready =
    department != null && position != null && salary.trim() !== '' && Number.isFinite(parsedSalary) && parsedSalary > 0 && currency.trim() !== '' && effectiveFrom !== ''

  const transfer = useMutation({
    mutationFn: () =>
      transferEmployee(card.id, {
        departmentId: department?.id ?? null,
        positionId: position,
        salary: parsedSalary,
        currency: currency.trim(),
        effectiveFrom,
        orderNumber: orderNumber.trim() === '' ? null : orderNumber.trim(),
      }),
    onSuccess: () => onDone(effectiveFrom),
    onError: (error) => setFailure(failureText(error, t)),
  })

  return (
    <FormModal
      open
      sheet
      overline={t('record.hr.transfer.overline')}
      title={t('record.hr.transfer.title', { name: card.fullName })}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('card.cancel')}
          </button>
          <button type="button" className="btn btn-primary" disabled={!ready || transfer.isPending} onClick={() => transfer.mutate()}>
            {t('record.hr.transfer.submit')}
          </button>
        </>
      }
    >
      <div className="alert alert-neutral" style={{ padding: '6px 10px' }}>
        <span>
          {t('record.hr.transfer.now', {
            position: labelOf(values, 'position_id') ?? '—',
            department: labelOf(values, 'department_id') ?? '—',
            salary: money(salaryNow, current?.currency ?? card.currency, locale),
          })}
        </span>
      </div>
      {failure && (
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span>{failure}</span>
        </div>
      )}
      <Field id="hr-transfer-position" label={t('card.hr.emp.position')} required>
        <select
          id="hr-transfer-position"
          className="input"
          value={position ?? ''}
          disabled={!positions.data}
          onChange={(event) => setPosition(event.target.value === '' ? null : event.target.value)}
        >
          {/* Until the directory arrives the current position is the only honest option. */}
          {!positions.data && <option value={position ?? ''}>{labelOf(values, 'position_id') ?? '—'}</option>}
          {(positions.data ?? []).map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </Field>
      {positions.isError && <span className="help">{t('record.hr.transfer.positionsFailed')}</span>}
      <Field id="hr-transfer-department" label={t('column.department_id')} required>
        <RecordPicker id="hr-transfer-department" entity="hr.department" searchColumn="name" value={department} onChange={setDepartment} />
      </Field>
      <div className="form-grid">
        <Field id="hr-transfer-salary" label={t('card.hr.emp.salary')} required>
          <input id="hr-transfer-salary" className="input num" inputMode="decimal" value={salary} onChange={(event) => setSalary(event.target.value)} />
        </Field>
        <Field id="hr-transfer-currency" label={t('column.currency')} required>
          <input id="hr-transfer-currency" className="input mono" value={currency} maxLength={3} onChange={(event) => setCurrency(event.target.value.toUpperCase())} />
        </Field>
        <Field id="hr-transfer-date" label={t('record.hr.transfer.date')} required>
          <input id="hr-transfer-date" className="input" type="date" value={effectiveFrom} onChange={(event) => setEffectiveFrom(event.target.value)} />
        </Field>
        <Field id="hr-transfer-order" label={t('card.hr.emp.order')}>
          <input id="hr-transfer-order" className="input mono" value={orderNumber} onChange={(event) => setOrderNumber(event.target.value)} />
        </Field>
      </div>
    </FormModal>
  )
}

/**
 * Увольнение: последний рабочий день и номер приказа.
 *
 * Необратимое — фокус на «Отмена», кнопка красная. Последний день, а не дата приказа: по нему
 * считается расчёт и блокируется учётная запись.
 */
function TerminateDialog({ card, onClose, onDone }: { card: EmployeeCard; onClose: () => void; onDone: (lastDay: string) => void }) {
  const { t } = useApp()
  const [lastDay, setLastDay] = useState('')
  const [orderNumber, setOrderNumber] = useState('')
  const [failure, setFailure] = useState<string | null>(null)
  const ready = lastDay !== '' && orderNumber.trim() !== ''
  const terminate = useMutation({
    mutationFn: () => terminateEmployee(card.id, { lastDay, orderNumber: orderNumber.trim() }),
    onSuccess: () => onDone(lastDay),
    onError: (error) => setFailure(failureText(error, t)),
  })
  return (
    <FormModal
      open
      danger
      title={t('record.hr.terminate.title', { name: card.fullName })}
      subtitle={card.personnelNumber ? t('record.hr.emp.personnelNumberShort', { number: card.personnelNumber }) : undefined}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" data-autofocus onClick={onClose}>
            {t('card.cancel')}
          </button>
          <button type="button" className="btn btn-danger" disabled={!ready || terminate.isPending} onClick={() => terminate.mutate()}>
            {t('record.hr.emp.terminateConfirm')}
          </button>
        </>
      }
    >
      {failure && (
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span>{failure}</span>
        </div>
      )}
      <div className="form-grid">
        <Field id="hr-terminate-day" label={t('record.hr.terminate.lastDay')} required>
          <input id="hr-terminate-day" className="input" type="date" value={lastDay} onChange={(event) => setLastDay(event.target.value)} />
        </Field>
        <Field id="hr-terminate-order" label={t('card.hr.emp.order')} required>
          <input id="hr-terminate-order" className="input mono" value={orderNumber} onChange={(event) => setOrderNumber(event.target.value)} />
        </Field>
      </div>
      <p className="t-sm">
        {t('record.hr.terminate.text')} <span className="strong">{t('record.irreversible')}</span>
      </p>
    </FormModal>
  )
}

/**
 * Премия: сначала расчёт, потом начисление.
 *
 * Фонд обязателен — сервер без него отвечает 400, а подставить умолчание нельзя: премиальный
 * фонд это решение о деньгах. Коэффициент и сумму считает сервер; показывается и разбор по
 * показателям — премия без разбора это цифра, которую получатель не может проверить. Веса, не
 * дающие в сумме 1, — отказ сервера его словами, а не тихая нормализация.
 */
export function BonusDialog({
  employeeId,
  name,
  period,
  onClose,
  onDone,
}: {
  employeeId: string
  name: string
  period: { from: string; to: string }
  onClose: () => void
  onDone: (bonus: BonusView) => void
}) {
  const { t, locale } = useApp()
  const [from, setFrom] = useState(period.from)
  const [to, setTo] = useState(period.to)
  const [fund, setFund] = useState('')
  const [failure, setFailure] = useState<string | null>(null)
  const [preview, setPreview] = useState<BonusView | null>(null)
  const parsedFund = Number(fund.replace(',', '.').replace(/\s/g, ''))
  const ready = from !== '' && to !== '' && fund.trim() !== '' && Number.isFinite(parsedFund) && parsedFund > 0

  const calculate = useMutation({
    mutationFn: () => fetchBonus(employeeId, from, to, parsedFund),
    onSuccess: (result) => {
      setFailure(null)
      setPreview(result)
    },
    onError: (error) => {
      setPreview(null)
      setFailure(failureText(error, t))
    },
  })
  const settle = useMutation({
    mutationFn: () => settleBonus(employeeId, { from, to, fund: parsedFund }),
    onSuccess: onDone,
    onError: (error) => setFailure(failureText(error, t)),
  })
  // A changed input invalidates the preview: settling a sum computed for other numbers would
  // pay what nobody looked at.
  const edit = (setter: (value: string) => void) => (value: string) => {
    setter(value)
    setPreview(null)
  }

  return (
    <FormModal
      open
      title={t('record.hr.bonus.title', { name })}
      subtitle={t('record.hr.bonus.subtitle')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('card.cancel')}
          </button>
          {preview && preview.bonus != null ? (
            <button type="button" className="btn btn-primary" disabled={settle.isPending} onClick={() => settle.mutate()}>
              {t('record.hr.bonus.settle', { amount: money(preview.bonus, null, locale) })}
            </button>
          ) : (
            <button type="button" className="btn btn-primary" disabled={!ready || calculate.isPending} onClick={() => calculate.mutate()}>
              {t('record.hr.bonus.calculate')}
            </button>
          )}
        </>
      }
    >
      {failure && (
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span>{failure}</span>
        </div>
      )}
      <div className="form-grid">
        <Field id="hr-bonus-from" label={t('record.hr.bonus.from')} required>
          <input id="hr-bonus-from" className="input" type="date" value={from} onChange={(event) => edit(setFrom)(event.target.value)} />
        </Field>
        <Field id="hr-bonus-to" label={t('record.hr.bonus.to')} required>
          <input id="hr-bonus-to" className="input" type="date" value={to} onChange={(event) => edit(setTo)(event.target.value)} />
        </Field>
      </div>
      <Field id="hr-bonus-fund" label={t('record.hr.bonus.fund')} required help={t('record.hr.bonus.fundHelp')}>
        <input id="hr-bonus-fund" className="input num" inputMode="decimal" value={fund} onChange={(event) => edit(setFund)(event.target.value)} />
      </Field>
      {preview && (
        <div className="stack hr-bonus-preview" style={{ gap: 8 }} role="status">
          <span className="overline">{t('record.hr.bonus.preview')}</span>
          <div className="row-wrap" style={{ gap: '6px 10px' }}>
            <span className="secondary">{t('record.hr.bonus.fund')}</span>
            <span className="strong">{money(preview.fund, null, locale)}</span>
            <span className="muted">×</span>
            <span className="secondary">{t('record.hr.bonus.coefficient')}</span>
            <span className="strong">{num(preview.achievement, locale)}</span>
            <span className="muted">=</span>
            <span className="t-md strong">{money(preview.bonus, null, locale)}</span>
          </div>
          {preview.lines.length > 0 && (
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('card.hr.kpi.metric')}</th>
                  <th className="num">{t('card.hr.kpi.weight')}</th>
                  <th className="num">{t('card.hr.kpi.completion')}</th>
                </tr>
              </thead>
              <tbody>
                {preview.lines.map((line) => (
                  <tr key={line.code}>
                    <td className="wrap">{line.name}</td>
                    <td className="num">{percent(line.weight * 100, locale)}</td>
                    <td className="num">{percent(line.completionPercent, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </FormModal>
  )
}

// ------------------------------------------------------------------ расчёты показа

/** Стаж словами: «4 г. 7 мес.». Календарь, а не учёт: стаж для отпуска считает сервер. */
function tenure(hiredOn: string, t: Translate, now: Date = new Date()): string {
  const start = parseDay(hiredOn)
  if (!start) return '—'
  if (start.getTime() > now.getTime()) return t('record.hr.emp.notStarted')
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth())
  if (now.getDate() < start.getDate()) months -= 1
  const years = Math.floor(months / 12)
  const rest = months % 12
  if (years === 0 && rest === 0) return t('record.hr.emp.lessThanMonth')
  return [years > 0 ? t('record.hr.emp.years', { n: years }) : null, rest > 0 ? t('record.hr.emp.months', { n: rest }) : null]
    .filter(Boolean)
    .join(' ')
}

function tenureLine(card: EmployeeCard, locale: string, t: Translate): string {
  if (!card.hiredOn) return ''
  const start = parseDay(card.hiredOn)
  if (start && start.getTime() > Date.now()) return t('record.hr.emp.startsOn', { date: day(card.hiredOn, locale as 'ru') })
  return t('record.hr.emp.tenureLine', { tenure: tenure(card.hiredOn, t), date: day(card.hiredOn, locale as 'ru') })
}

/**
 * Остаток основного отпуска за текущий год и прочие виды отдельно — не сложенные: у
 * дополнительного и учебного свои правила, и сумма отвечала бы на вопрос, которого не задавали.
 */
function currentBalance(rows: BalanceView[]): { main: BalanceView; others: BalanceView[] } | null {
  const year = new Date().getFullYear()
  const thisYear = rows.filter((row) => row.year === year)
  const main = thisYear.find((row) => row.type === 'annual')
  if (!main) return null
  return { main, others: thisYear.filter((row) => row !== main && row.remaining > 0) }
}
